import React, { useState, useEffect, useMemo } from 'react';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_API_BASE_URL || '';

const MONTH_NAMES = ['', 'Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];

const tr = (s) => {
  if (!s) return '';
  return String(s)
    .replace(/ç/g,'c').replace(/Ç/g,'C')
    .replace(/ğ/g,'g').replace(/Ğ/g,'G')
    .replace(/ı/g,'i').replace(/İ/g,'I')
    .replace(/ö/g,'o').replace(/Ö/g,'O')
    .replace(/ş/g,'s').replace(/Ş/g,'S')
    .replace(/ü/g,'u').replace(/Ü/g,'U');
};

const fmtPeriod = (d) => {
  const s = String(d);
  if (s.length >= 6) {
    const y = s.substring(0, 4);
    const m = parseInt(s.substring(4), 10);
    return `${MONTH_NAMES[m] || m} ${y}`;
  }
  return s;
};

const fmtNum = (n) => {
  if (n === null || n === undefined) return '-';
  return parseFloat(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
};

const C = {
  bg: '#f8fafc', card: '#fff', border: '#e2e8f0', text: '#1e293b',
  textMuted: '#64748b', accent: '#2563eb', accentLight: '#dbeafe',
  green: '#059669', greenBg: '#ecfdf5', amber: '#d97706', amberBg: '#fffbeb',
  red: '#dc2626', redBg: '#fef2f2',
};

const ASKIMonthlyReport = () => {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/aski/analysis/periods`);
        const d = await res.json();
        setPeriods(d.periods || []);
        if (d.periods && d.periods.length > 0) {
          setSelectedPeriod(String(d.periods[0]));
        }
      } catch (e) {
        setError('Donem listesi yuklenemedi.');
      } finally {
        setMetaLoading(false);
      }
    })();
  }, []);

  const loadReport = async () => {
    if (!selectedPeriod) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API}/api/aski/analysis/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startPeriod: selectedPeriod,
          endPeriod: selectedPeriod,
          groupBy: 'raw',
        }),
      });
      const d = await res.json();
      setReportData(d.rows || []);
    } catch (e) {
      setError('Rapor verisi yuklenemedi: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const analysis = useMemo(() => {
    if (!reportData || reportData.length === 0) return null;

    const totalConsumption = reportData.reduce((s, r) => s + (parseFloat(r.m3_tuketim) || 0), 0);
    const uniqueAboneler = [...new Set(reportData.map(r => r.aboneno))];
    const uniqueIlceler = [...new Set(reportData.map(r => r.ilce_adi).filter(Boolean))];
    const avgConsumption = totalConsumption / uniqueAboneler.length;

    const byAbone = {};
    reportData.forEach(r => {
      const key = r.aboneno;
      if (!byAbone[key]) {
        byAbone[key] = {
          aboneno: r.aboneno,
          abone_adi: r.abone_adi,
          ilce: r.ilce_adi,
          mahalle: r.mahalle_adi,
          tuketim: 0,
          kayitSayisi: 0,
        };
      }
      byAbone[key].tuketim += parseFloat(r.m3_tuketim) || 0;
      byAbone[key].kayitSayisi += 1;
    });

    const parkList = Object.values(byAbone).sort((a, b) => b.tuketim - a.tuketim);

    const byIlce = {};
    reportData.forEach(r => {
      const ilce = r.ilce_adi || 'Bilinmiyor';
      if (!byIlce[ilce]) byIlce[ilce] = { ilce, tuketim: 0, aboneSayisi: new Set() };
      byIlce[ilce].tuketim += parseFloat(r.m3_tuketim) || 0;
      byIlce[ilce].aboneSayisi.add(r.aboneno);
    });
    const ilceList = Object.values(byIlce)
      .map(i => ({ ...i, aboneSayisi: i.aboneSayisi.size }))
      .sort((a, b) => b.tuketim - a.tuketim);

    const top10 = parkList.slice(0, 10);
    const bottom10 = parkList.filter(p => p.tuketim > 0).slice(-10).reverse();

    return {
      totalConsumption,
      uniqueAboneler: uniqueAboneler.length,
      uniqueIlceler: uniqueIlceler.length,
      avgConsumption,
      parkList,
      ilceList,
      top10,
      bottom10,
      kayitSayisi: reportData.length,
    };
  }, [reportData]);

  const generatePDF = async () => {
    if (!analysis || !reportData) return;
    try {
      setGenerating(true);
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210, H = 297;
      const now = new Date();
      const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const periodLabel = tr(fmtPeriod(selectedPeriod));

      const blue = [0, 75, 145];
      const darkBlue = [0, 50, 100];
      const gray = [100, 116, 139];
      const dark = [30, 41, 59];
      const white = [255, 255, 255];
      const red = [220, 38, 38];
      const green = [16, 185, 129];

      const drawWave = (yy, hh, op) => {
        doc.setFillColor(200, 225, 250);
        doc.setGState(new doc.GState({ opacity: op }));
        const steps = 40, sw = W / steps;
        for (let i = 0; i <= steps; i++) {
          const x = i * sw;
          const wy = yy + Math.sin(i * 0.4) * 4 + Math.cos(i * 0.2) * 3;
          doc.rect(x, wy, sw + 0.5, hh, 'F');
        }
        doc.setGState(new doc.GState({ opacity: 1 }));
      };

      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      const logoOk = await new Promise(r => {
        logoImg.onload = () => r(true);
        logoImg.onerror = () => r(false);
        logoImg.src = `${window.location.origin}/logo.png`;
      });

      const drawHeader = (title) => {
        doc.setFillColor(245, 249, 255);
        doc.rect(0, 0, W, 20, 'F');
        doc.setDrawColor(200, 215, 235);
        doc.setLineWidth(0.3);
        doc.line(0, 20, W, 20);
        if (logoOk) doc.addImage(logoImg, 'PNG', 8, 2, 32, 16);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...gray);
        doc.text(`ASKI Aylik Rapor - ${periodLabel}`, 44, 11);
        doc.setFillColor(...blue);
        doc.rect(0, 24, W, 12, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...white);
        doc.text(tr(title), 15, 32);
      };

      // ===== KAPAK =====
      doc.setFillColor(245, 249, 255);
      doc.rect(0, 0, W, H, 'F');
      drawWave(30, 60, 0.15);
      drawWave(180, 80, 0.12);
      drawWave(230, 67, 0.08);
      if (logoOk) doc.addImage(logoImg, 'PNG', 15, 12, 50, 25);

      doc.setFillColor(...white);
      doc.setGState(new doc.GState({ opacity: 0.95 }));
      doc.roundedRect(25, 90, 160, 95, 8, 8, 'F');
      doc.setGState(new doc.GState({ opacity: 1 }));
      if (logoOk) doc.addImage(logoImg, 'PNG', 75, 95, 60, 30);

      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkBlue);
      doc.text('ASKI SU TUKETIMI', 105, 140, { align: 'center' });
      doc.text('AYLIK RAPOR', 105, 151, { align: 'center' });

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(12);
      doc.setTextColor(...gray);
      doc.text(periodLabel, 105, 165, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`${analysis.uniqueAboneler} Abone | ${analysis.uniqueIlceler} Ilce`, 105, 175, { align: 'center' });

      // ===== SAYFA 2: OZET =====
      doc.addPage();
      drawHeader('1. GENEL TUKETIM OZETI');
      let y = 46;

      const cards = [
        { label: 'Toplam Tuketim', value: `${fmtNum(analysis.totalConsumption)} m3`, color: blue },
        { label: 'Toplam Abone', value: `${analysis.uniqueAboneler}`, color: green },
        { label: 'Ilce Sayisi', value: `${analysis.uniqueIlceler}`, color: [245, 158, 11] },
        { label: 'Ort. Tuketim', value: `${fmtNum(analysis.avgConsumption)} m3`, color: [99, 102, 241] },
      ];
      const cw = 42, cg = 4, sx = 15;
      cards.forEach((c, i) => {
        const cx = sx + i * (cw + cg);
        doc.setFillColor(250, 252, 255);
        doc.setDrawColor(220, 230, 245);
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, y, cw, 28, 3, 3, 'FD');
        doc.setFillColor(...c.color);
        doc.circle(cx + 8, y + 10, 4.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...white);
        doc.text(c.label.charAt(0), cx + 8, y + 11.5, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...gray);
        doc.text(tr(c.label), cx + 15, y + 9);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...c.color);
        doc.text(tr(c.value), cx + 15, y + 19);
      });
      y += 38;

      // Ilce tablosu
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...darkBlue);
      doc.text('Ilce Bazli Tuketim', 15, y);
      y += 6;

      const ilceCols = [55, 30, 45, 30];
      const ilceHeaders = ['Ilce', 'Abone', 'Tuketim (m3)', 'Oran (%)'];
      doc.setFillColor(...blue);
      doc.rect(15, y, ilceCols.reduce((a, b) => a + b, 0), 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...white);
      let hx = 15;
      ilceHeaders.forEach((h, i) => { doc.text(h, hx + 2, y + 5.5); hx += ilceCols[i]; });
      y += 8;

      analysis.ilceList.forEach((row, idx) => {
        if (y > H - 50) { doc.addPage(); drawHeader('1. GENEL TUKETIM OZETI (devam)'); y = 42; }
        if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, ilceCols.reduce((a, b) => a + b, 0), 7, 'F'); }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...dark);
        let rx = 15;
        const pct = ((row.tuketim / analysis.totalConsumption) * 100).toFixed(1);
        const vals = [tr(row.ilce), String(row.aboneSayisi), fmtNum(row.tuketim), pct + '%'];
        vals.forEach((v, i) => { doc.text(v, rx + 2, y + 5); rx += ilceCols[i]; });
        y += 7;
      });

      // ===== SAYFA 3: EN COK 10 =====
      doc.addPage();
      drawHeader('2. EN COK SU TUKETEN 10 PARK');
      y = 42;

      const topCols = [8, 65, 30, 35, 35];
      const topHeaders = ['#', 'Park / Abone Adi', 'Abone No', 'Ilce', 'Tuketim (m3)'];
      doc.setFillColor(...red);
      doc.rect(15, y, topCols.reduce((a, b) => a + b, 0), 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...white);
      hx = 15;
      topHeaders.forEach((h, i) => { doc.text(h, hx + 2, y + 5.5); hx += topCols[i]; });
      y += 8;

      analysis.top10.forEach((p, i) => {
        const bg = i < 3 ? [254, 242, 242] : (i % 2 === 0 ? [248, 250, 252] : [255, 255, 255]);
        doc.setFillColor(...bg);
        doc.rect(15, y, topCols.reduce((a, b) => a + b, 0), 7, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...dark);
        let rx = 15;
        const vals = [String(i + 1), tr(String(p.abone_adi || '').substring(0, 28)), String(p.aboneno || ''), tr(String(p.ilce || '').substring(0, 14)), fmtNum(p.tuketim)];
        vals.forEach((v, ci) => {
          if (ci === 4) { doc.setFont('helvetica', 'bold'); doc.setTextColor(...red); }
          doc.text(v, rx + 2, y + 5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...dark);
          rx += topCols[ci];
        });
        y += 7;
      });

      // En az 10
      y += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...green);
      doc.text('En Az Su Tuketen 10 Park', 15, y);
      y += 6;

      doc.setFillColor(...green);
      doc.rect(15, y, topCols.reduce((a, b) => a + b, 0), 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...white);
      hx = 15;
      topHeaders.forEach((h, i) => { doc.text(h, hx + 2, y + 5.5); hx += topCols[i]; });
      y += 8;

      analysis.bottom10.forEach((p, i) => {
        if (i % 2 === 0) { doc.setFillColor(240, 253, 244); doc.rect(15, y, topCols.reduce((a, b) => a + b, 0), 7, 'F'); }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...dark);
        let rx = 15;
        const vals = [String(i + 1), tr(String(p.abone_adi || '').substring(0, 28)), String(p.aboneno || ''), tr(String(p.ilce || '').substring(0, 14)), fmtNum(p.tuketim)];
        vals.forEach((v, ci) => { doc.text(v, rx + 2, y + 5); rx += topCols[ci]; });
        y += 7;
      });

      // ===== SAYFA 4+: TUM PARKLAR =====
      doc.addPage();
      drawHeader('3. TUM PARKLAR - DETAYLI DOKÜM');
      y = 42;

      const allCols = [8, 20, 55, 30, 35, 25];
      const allHeaders = ['#', 'Abone No', 'Park / Abone Adi', 'Ilce', 'Mahalle', 'm3'];

      const drawAllHeader = () => {
        doc.setFillColor(...blue);
        doc.rect(15, y, allCols.reduce((a, b) => a + b, 0), 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...white);
        let hx2 = 15;
        allHeaders.forEach((h, i) => { doc.text(h, hx2 + 1, y + 5.5); hx2 += allCols[i]; });
        y += 8;
      };
      drawAllHeader();

      analysis.parkList.forEach((p, i) => {
        if (y > H - 30) { doc.addPage(); drawHeader('3. TUM PARKLAR (devam)'); y = 42; drawAllHeader(); }
        if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, allCols.reduce((a, b) => a + b, 0), 6, 'F'); }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(...dark);
        let rx = 15;
        const vals = [
          String(i + 1),
          String(p.aboneno || ''),
          tr(String(p.abone_adi || '').substring(0, 25)),
          tr(String(p.ilce || '').substring(0, 12)),
          tr(String(p.mahalle || '').substring(0, 14)),
          fmtNum(p.tuketim),
        ];
        vals.forEach((v, ci) => { doc.text(v, rx + 1, y + 4); rx += allCols[ci]; });
        y += 6;
      });

      // ===== FOOTER TUM SAYFALAR =====
      const tp = doc.internal.getNumberOfPages();
      for (let p = 1; p <= tp; p++) {
        doc.setPage(p);
        const fy = H - 20;
        doc.setDrawColor(200, 215, 235);
        doc.setLineWidth(0.3);
        doc.line(15, fy, W - 15, fy);
        doc.setFillColor(...blue);
        doc.circle(20, fy + 6, 1.5, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...gray);
        doc.text('www.pronuve.com', 24, fy + 8);
        doc.text(`Olusturulma: ${dateStr}`, 85, fy + 8);
        doc.text('info@pronuve.com', W - 15, fy + 8, { align: 'right' });
        if (p > 1) doc.text(`Sayfa ${p - 1} / ${tp - 1}`, 105, fy + 13, { align: 'center' });
      }

      doc.save(`ASKI_Rapor_${selectedPeriod}_${dateStr.replace(/\./g, '')}.pdf`);
    } catch (e) {
      setError('PDF olusturma hatasi: ' + e.message);
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const containerStyle = {
    padding: '24px', maxWidth: 1100, margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };
  const cardStyle = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '20px 24px', marginBottom: 16,
  };
  const headingStyle = { fontSize: 18, fontWeight: 600, color: C.text, margin: '0 0 4px' };
  const subStyle = { fontSize: 13, color: C.textMuted, margin: 0 };
  const selectStyle = {
    padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 6,
    fontSize: 14, color: C.text, background: C.card, minWidth: 180, outline: 'none',
  };
  const btnPrimary = {
    padding: '8px 20px', background: C.accent, color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };
  const btnSecondary = {
    padding: '8px 20px', background: C.greenBg, color: C.green, border: `1px solid ${C.green}`,
    borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };
  const statBox = (bg) => ({
    flex: '1 1 180px', background: bg, borderRadius: 8, padding: '16px 20px', textAlign: 'center',
  });
  const tableStyle = {
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
  };
  const thStyle = {
    textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${C.border}`,
    fontSize: 12, fontWeight: 600, color: C.textMuted, background: '#f9fafb',
  };
  const tdStyle = {
    padding: '7px 10px', borderBottom: `1px solid #f1f5f9`, color: C.text,
  };

  if (metaLoading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}><p style={{ color: C.textMuted }}>Donemler yukleniyor...</p></div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>ASKI Aylik Su Tuketim Raporu</h2>
        <p style={subStyle}>Sectiginiz doneme ait detayli su tuketim raporu olusturun ve PDF olarak indirin.</p>
      </div>

      {/* Period Selection */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: C.text }}>Donem:</label>
        <select
          style={selectStyle}
          value={selectedPeriod}
          onChange={e => { setSelectedPeriod(e.target.value); setReportData(null); }}
        >
          {periods.map(p => (
            <option key={p} value={String(p)}>{fmtPeriod(p)}</option>
          ))}
        </select>
        <button
          style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}
          onClick={loadReport}
          disabled={loading || !selectedPeriod}
        >
          {loading ? 'Yukleniyor...' : 'Rapor Verisi Yukle'}
        </button>
        {analysis && (
          <button
            style={{ ...btnSecondary, opacity: generating ? 0.6 : 1 }}
            onClick={generatePDF}
            disabled={generating}
          >
            {generating ? 'PDF Olusturuluyor...' : 'PDF Indir'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ ...cardStyle, background: C.redBg, borderColor: C.red, color: C.red, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Summary Stats */}
      {analysis && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={statBox(C.accentLight)}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.accent }}>{fmtNum(analysis.totalConsumption)}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Toplam Tuketim (m3)</div>
            </div>
            <div style={statBox(C.greenBg)}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>{analysis.uniqueAboneler}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Toplam Abone</div>
            </div>
            <div style={statBox(C.amberBg)}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.amber }}>{analysis.uniqueIlceler}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Ilce Sayisi</div>
            </div>
            <div style={statBox('#f0f4ff')}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#4338ca' }}>{fmtNum(analysis.avgConsumption)}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Ort. Tuketim / Abone (m3)</div>
            </div>
          </div>

          {/* Ilce Bazli */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 12 }}>Ilce Bazli Tuketim</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Ilce</th>
                    <th style={thStyle}>Abone Sayisi</th>
                    <th style={thStyle}>Toplam Tuketim (m3)</th>
                    <th style={thStyle}>Oran</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.ilceList.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={tdStyle}>{row.ilce}</td>
                      <td style={tdStyle}>{row.aboneSayisi}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtNum(row.tuketim)}</td>
                      <td style={tdStyle}>{((row.tuketim / analysis.totalConsumption) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top 10 */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.red, marginBottom: 12 }}>En Cok Su Tuketen 10 Park</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Park / Abone</th>
                    <th style={thStyle}>Abone No</th>
                    <th style={thStyle}>Ilce</th>
                    <th style={thStyle}>Tuketim (m3)</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.top10.map((p, i) => (
                    <tr key={i} style={{ background: i < 3 ? C.redBg : (i % 2 === 0 ? '#fff' : '#fafbfc') }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{i + 1}</td>
                      <td style={tdStyle}>{p.abone_adi || '-'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{p.aboneno}</td>
                      <td style={tdStyle}>{p.ilce || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: C.red }}>{fmtNum(p.tuketim)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom 10 */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.green, marginBottom: 12 }}>En Az Su Tuketen 10 Park</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Park / Abone</th>
                    <th style={thStyle}>Abone No</th>
                    <th style={thStyle}>Ilce</th>
                    <th style={thStyle}>Tuketim (m3)</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.bottom10.map((p, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}>{p.abone_adi || '-'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{p.aboneno}</td>
                      <td style={tdStyle}>{p.ilce || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: C.green }}>{fmtNum(p.tuketim)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Full Park List */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              Tum Parklar - Detayli Doküm ({analysis.parkList.length} abone)
            </h3>
            <p style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Tuketim sirasina gore (yuksekten dusuge)</p>
            <div style={{ overflowX: 'auto', maxHeight: 500 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>#</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Abone No</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Park / Abone</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Ilce</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Mahalle</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Tuketim (m3)</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.parkList.map((p, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ ...tdStyle, color: C.textMuted, fontSize: 12 }}>{i + 1}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{p.aboneno}</td>
                      <td style={tdStyle}>{p.abone_adi || '-'}</td>
                      <td style={tdStyle}>{p.ilce || '-'}</td>
                      <td style={tdStyle}>{p.mahalle || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtNum(p.tuketim)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!analysis && !loading && !error && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40, color: C.textMuted }}>
          <p style={{ fontSize: 14 }}>Bir donem secip "Rapor Verisi Yukle" butonuna basin.</p>
          <p style={{ fontSize: 12 }}>Yuklenen veri uzerinden rapor onizleme gorecek ve PDF olarak indirebileceksiniz.</p>
        </div>
      )}
    </div>
  );
};

export default ASKIMonthlyReport;
